/* ===================== ADD LESSON MODAL =====================
   Popup so instructors can add lessons to a course without leaving the
   dashboard — POST /courses/{course}/lessons. MODULE-FIRST flow: modules
   (topics) are created beforehand via CreateTopicModal, and every lesson
   attaches to one of them (topic_id required). A course with no modules
   shows a "create a module first" notice instead of the module select.

   Per the API: title + type are required. A "vimeo" lesson carries its
   video via vimeo_id; a "text" lesson its body, a "youtube" lesson its
   link and a "file" lesson its uploaded file's URL via content.

   Opened from the course details page — `course` pre-selects and locks the
   course; `topic` (optional) pre-selects and locks the module when launched
   from a specific module block. Building a curriculum is repetitive, so a
   successful add keeps the dialog open — it resets just the lesson fields
   (keeping the course + module) and shows a confirmation, ready for the
   next lesson. Same form idiom, skin and 422 handling as the other academy
   modals. */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Select from 'react-select'
import { api } from '@astro/api'
import { Controller, useForm } from '@astro/forms'
import { selectStyles } from '@astro/astrologer-forms'
import { useInstructorCourses, useCourseTopics } from '../useDashboard'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

/* Mirrors the admin panel's lesson types: Video (Vimeo) · Video (YouTube) ·
   Text / Article · File / Resource. A file lesson uploads its attachment first
   (POST /courses/{course}/lesson-file-upload) and is saved with the returned
   URL as its content, exactly like the admin's Add Lesson card. */
const TYPE_OPTIONS = [
  { value: 'vimeo', label: '🎬 Video (Vimeo)' },
  { value: 'youtube', label: '▶️ Video (YouTube)' },
  { value: 'text', label: '📝 Text / Article' },
  { value: 'file', label: '📎 File / Resource' },
]

// Same link shapes the API turns into an embed URL (watch?v=, youtu.be/, shorts/, live/, embed/).
const YOUTUBE_LINK = /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)[\w-]{11}/

// Same limits as the API's upload (utils/upload.ts): PDF, JPG or PNG, 5 MB.
const FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png'
const FILE_MAX_BYTES = 5 * 1024 * 1024

const fileNameFromUrl = (url) => decodeURIComponent(String(url).split('/').pop() || url)

function Field({ label, error, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** react-select wired to a react-hook-form Controller, matching the skin used
 * across the dashboard's create modals. */
function SelectField({ control, name, options, placeholder, isLoading, isDisabled, rules, error, inputId }) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <Select
          inputId={inputId}
          options={options}
          value={options.find((o) => String(o.value) === String(field.value)) ?? null}
          onChange={(option) => field.onChange(option?.value ?? '')}
          onBlur={field.onBlur}
          placeholder={placeholder}
          styles={selectStyles}
          isLoading={isLoading}
          isDisabled={isDisabled}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          className={error ? 'rounded-xl ring-1 ring-red-300' : undefined}
        />
      )}
    />
  )
}

export default function CreateLessonModal({ onClose, course = null, topic = null }) {
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null)
  const [success, setSuccess] = useState(null)

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      course_id: course?.id ?? '',
      title: '',
      type: 'vimeo',
      vimeo_id: '',
      content: '',
      topic_id: topic?.id ?? '',
      is_preview: false,
    },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  // The course is fixed when the modal was opened from a course page; otherwise
  // the instructor picks from their own courses here.
  const { courses, isLoading: coursesLoading } = useInstructorCourses()
  const courseOptions = courses.map((c) => ({ value: c.id, label: c.title }))
  const courseLocked = Boolean(course?.id)
  const topicLocked = Boolean(topic?.id)

  const selectedCourseId = watch('course_id')
  const type = watch('type')

  // Existing modules of the chosen course — the only place a lesson can live.
  const { topics, isLoading: topicsLoading } = useCourseTopics(selectedCourseId)
  // A just-locked topic may not be in the cached list yet — seed it in.
  const topicOptions =
    topicLocked && !topics.some((t) => String(t.value) === String(topic.id))
      ? [{ value: topic.id, label: topic.title || `Module ${topic.id}` }, ...topics]
      : topics
  const noModules = Boolean(selectedCourseId) && !topicsLoading && topicOptions.length === 0

  // `content` means something different per type (text body, YouTube link,
  // uploaded file URL), so switching type starts it fresh.
  const previousType = useRef(type)
  useEffect(() => {
    if (previousType.current === type) return
    previousType.current = type
    setValue('content', '')
    clearErrors(['content', 'vimeo_id'])
  }, [type, setValue, clearErrors])

  // File lessons: the attachment is uploaded on pick; its URL becomes `content`.
  const fileInputRef = useRef(null)
  const fileUrl = watch('content')
  const uploadMutation = useMutation({
    mutationFn: ({ courseId, file }) => api.courses.uploadLessonFile(courseId, file),
    onSuccess: (res) => {
      const url = res?.data?.file_url
      if (!url) {
        setError('content', { type: 'server', message: 'The upload did not return a file URL. Please try again.' })
        return
      }
      setValue('content', url)
      clearErrors('content')
    },
    onError: (err) => {
      const details = err?.error?.details ?? err?.details
      const msg = details?.lesson_file
      setError('content', {
        type: 'server',
        message: (Array.isArray(msg) ? msg[0] : msg) || err?.message || 'Could not upload the file. Please try again.',
      })
    },
  })
  const onPickFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > FILE_MAX_BYTES) {
      setError('content', { type: 'manual', message: 'The file must not be greater than 5 MB.' })
      return
    }
    setBanner(null)
    setSuccess(null)
    uploadMutation.mutate({ courseId: getValues('course_id'), file })
  }

  const createMutation = useMutation({
    mutationFn: ({ courseId, payload }) => api.courses.createLesson(courseId, payload),
    onSuccess: (_res, { courseId, lessonTitle }) => {
      // Refresh the shared course-detail read (details page curriculum +
      // this form's module select).
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail', String(courseId)] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'instructor-courses'] })
      setBanner(null)
      setSuccess(`“${lessonTitle}” added. Add another, or close when you're done.`)
      // Keep the course + module context; clear only the per-lesson fields.
      const v = getValues()
      reset({
        ...v,
        title: '',
        vimeo_id: '',
        content: '',
        is_preview: false,
      })
    },
    onError: (err) => {
      setSuccess(null)
      // ApiRequestError carries the API's per-field 422 messages on .error.details.
      const details = err?.error?.details ?? err?.details
      let fieldErrors = 0
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
          fieldErrors += 1
        }
      }
      // The fields already say what's wrong; the banner covers anything else.
      setBanner(fieldErrors ? null : err?.message || 'Could not add the lesson. Please try again.')
    },
  })

  const onSubmit = handleSubmit((v) => {
    setBanner(null)
    setSuccess(null)
    // Conditional fields mount/unmount with the lesson type, so validate them
    // here (against the live values) rather than via register() rules — an
    // unmounted field keeps its last rule and would fire stale. Clear prior
    // manual errors first so a since-fixed field stops showing one.
    clearErrors(['content', 'vimeo_id', 'topic_id'])
    let bad = false
    if (v.type === 'text' && !v.content?.trim()) {
      setError('content', { type: 'manual', message: 'Content is required for a text lesson' }); bad = true
    }
    if (v.type === 'vimeo' && !v.vimeo_id?.trim()) {
      setError('vimeo_id', { type: 'manual', message: 'A Vimeo URL or ID is required' }); bad = true
    }
    if (v.type === 'youtube') {
      if (!v.content?.trim()) {
        setError('content', { type: 'manual', message: 'A YouTube video link is required' }); bad = true
      } else if (!YOUTUBE_LINK.test(v.content)) {
        setError('content', { type: 'manual', message: 'Enter a valid YouTube video link' }); bad = true
      }
    }
    if (v.type === 'file' && !v.content) {
      setError('content', { type: 'manual', message: 'Upload a file for this lesson' }); bad = true
    }
    if (!v.topic_id) {
      setError('topic_id', { type: 'manual', message: 'Choose the module this lesson belongs to' }); bad = true
    }
    if (bad) return

    const courseId = Number(v.course_id)
    const payload = { title: v.title.trim(), type: v.type, topic_id: Number(v.topic_id) }
    if (v.type === 'vimeo') payload.vimeo_id = v.vimeo_id.trim()
    else payload.content = v.content.trim()
    payload.is_preview = Boolean(v.is_preview)
    createMutation.mutate({ courseId, payload, lessonTitle: payload.title })
  })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add Lesson"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">Add Lesson</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              {topicLocked
                ? <>Adding to module &ldquo;{topic.title}&rdquo;.</>
                : courseLocked
                  ? <>Adding to &ldquo;{course.title}&rdquo; — pick the module it belongs to.</>
                  : 'Add a lesson to one of your courses.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}
        {success && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-green-50 text-green-700 border border-green-100">
            ✓ {success}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {!courseLocked && (
            <Field label="Course" error={errors.course_id?.message} className="sm:col-span-2">
              <SelectField
                control={control}
                name="course_id"
                inputId="lesson-course"
                options={courseOptions}
                placeholder={coursesLoading ? 'Loading…' : 'Select a course'}
                isLoading={coursesLoading}
                rules={{ required: 'Choose a course' }}
                error={errors.course_id}
              />
            </Field>
          )}

          {/* Module-first: the lesson must live inside an existing module. */}
          <Field label="Module / Topic" error={errors.topic_id?.message} className="sm:col-span-2">
            {noModules ? (
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
                This course has no modules yet — close this and create a module first, then add lessons inside it.
              </p>
            ) : (
              <SelectField
                control={control}
                name="topic_id"
                inputId="lesson-topic"
                options={topicOptions}
                placeholder={
                  !selectedCourseId ? 'Select a course first' : topicsLoading ? 'Loading modules…' : 'Select a module'
                }
                isLoading={topicsLoading}
                isDisabled={!selectedCourseId || topicLocked}
                error={errors.topic_id}
              />
            )}
          </Field>

          <Field label="Lesson Title" error={errors.title?.message} className="sm:col-span-2">
            <input
              className={cls('title')}
              placeholder="e.g. Introduction to Planets"
              maxLength={255}
              {...register('title', {
                validate: (value) => Boolean(value?.trim()) || 'Lesson title is required',
                maxLength: { value: 255, message: 'Lesson title must not exceed 255 characters' },
              })}
            />
          </Field>

          <Field label="Lesson Type" error={errors.type?.message}>
            <SelectField
              control={control}
              name="type"
              inputId="lesson-type"
              options={TYPE_OPTIONS}
              placeholder="Select type"
              rules={{ required: 'Type is required' }}
              error={errors.type}
            />
          </Field>

          <Field label="Preview" className="flex flex-col justify-end">
            <label className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 cursor-pointer h-[46px]">
              <input type="checkbox" className="w-4 h-4 accent-amber-600" {...register('is_preview')} />
              <span className="text-sm text-astroDark font-semibold">Free preview</span>
            </label>
          </Field>

          {type === 'text' ? (
            <Field label="Content" error={errors.content?.message} className="sm:col-span-2">
              <textarea
                rows={5}
                className={`${cls('content')} resize-none`}
                placeholder="Write the lesson text / reading material…"
                {...register('content')}
              />
            </Field>
          ) : type === 'youtube' ? (
            <Field label="YouTube Video Link" error={errors.content?.message} className="sm:col-span-2">
              <input
                className={cls('content')}
                placeholder="https://www.youtube.com/watch?v=…"
                {...register('content')}
              />
            </Field>
          ) : type === 'file' ? (
            /* Uploaded on pick (POST /courses/{course}/lesson-file-upload); the
               returned URL is the lesson's content. */
            <Field label="Lesson File" error={errors.content?.message} className="sm:col-span-2">
              <div
                className={`rounded-xl border-2 border-dashed bg-stone-50/60 px-4 py-6 text-center ${
                  errors.content ? 'border-red-300' : 'border-stone-200'
                }`}
              >
                <div className="text-2xl mb-2 opacity-50">📎</div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={FILE_ACCEPT}
                  className="hidden"
                  aria-label="Lesson file"
                  onChange={onPickFile}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMutation.isPending || !selectedCourseId}
                  className="px-4 py-2 rounded-lg bg-astroDark text-white text-[10px] font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {uploadMutation.isPending ? 'Uploading…' : fileUrl ? 'Replace File' : 'Upload File'}
                </button>
                <p className="text-[11px] text-stone-400 mt-2">
                  {!selectedCourseId ? 'Select a course first' : 'PDF, JPG or PNG · up to 5 MB'}
                </p>
                {fileUrl && !uploadMutation.isPending && (
                  <p className="text-[11px] font-semibold text-green-700 mt-2 truncate">✓ Attached: {fileNameFromUrl(fileUrl)}</p>
                )}
              </div>
            </Field>
          ) : (
            <Field label="Vimeo URL or ID" error={errors.vimeo_id?.message} className="sm:col-span-2">
              <input
                className={cls('vimeo_id')}
                placeholder="https://vimeo.com/123456789"
                {...register('vimeo_id')}
              />
            </Field>
          )}

          <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              {success ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || noModules || uploadMutation.isPending}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Adding…' : 'Add Lesson'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Portal to <body> so the overlay escapes the dashboard's stacking context
  // and paints above the site's fixed header (z-50); z-[1200] clears the suite's fixed mobile bottom nav (z-100) and stays under the
  // react-select menu portal (z-1250) so dropdowns still open above the dialog.
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
